import { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import InputMask from 'react-input-mask'
import Icon from '../Icon'
import {
  ADMIN_DATETIME_TZ,
  buildAdminDateTimeIso,
  clampTimeInput,
  formatAdminDateTimeParts,
  isCompleteDate,
  type Meridiem,
  normalizeMeridiem,
  parseAdminDateTime,
} from '../../utils/datetimeInput'

dayjs.extend(utc)
dayjs.extend(timezone)

interface DateTimePickerProps {
  value?: string | null
  onChange?: (value: string | null) => void

  min?: string
  max?: string

  disabled?: boolean
  required?: boolean

  label?: string

  dateLabel?: string
  timeLabel?: string
  meridiemLabel?: string
}

const DateTimePicker = ({
  value,
  onChange,
  min,
  max,
  disabled = false,
  required = false,
  label,
  dateLabel = 'Date',
  timeLabel = 'Time',
  meridiemLabel = 'AM/PM',
}: DateTimePickerProps) => {
  const datePickerRef = useRef<HTMLInputElement>(null)

  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [ampm, setAmpm] = useState<Meridiem>('pm')

  const minDayjs = useMemo(() => (min ? dayjs(min).tz(ADMIN_DATETIME_TZ) : null), [min])
  const maxDayjs = useMemo(() => (max ? dayjs(max).tz(ADMIN_DATETIME_TZ) : null), [max])

  useEffect(() => {
    if (!value) {
      setDate('')
      setTime('')
      setAmpm('pm')
      return
    }

    const parts = formatAdminDateTimeParts(value)
    setDate(parts.date)
    setTime(parts.time)
    setAmpm(parts.ampm)
  }, [value])

  const emitChange = (nextDate: string, nextTime?: string, nextMeridiem?: Meridiem) => {
    const resolvedTime = nextTime ?? time
    const resolvedMeridiem = nextMeridiem ?? ampm
    const iso = buildAdminDateTimeIso(nextDate, resolvedTime, resolvedMeridiem)

    if (!iso) {
      return
    }

    const parsed = parseAdminDateTime(iso)
    if (!parsed) {
      return
    }

    if (minDayjs && parsed.isBefore(minDayjs)) {
      return
    }
    if (maxDayjs && parsed.isAfter(maxDayjs)) {
      return
    }

    onChange?.(iso)
  }

  const handleDatePickerChange = (val?: string) => {
    if (!val) {
      return
    }

    const parsedDate = dayjs.tz(val, 'YYYY-MM-DD', ADMIN_DATETIME_TZ).format('MM/DD/YYYY')
    setDate(parsedDate)
    emitChange(parsedDate, time, ampm)
  }

  const handleDateChange = (val?: string) => {
    if (!val) {
      return
    }

    let [mm, dd, yyyy] = val.split('/')

    if (mm) {
      let monthNum = Number(mm.replace('_', '0'))
      if (monthNum > 12) monthNum = 12
      mm = monthNum.toString().padStart(2, '0')
    }

    if (yyyy && yyyy.length === 4) {
      let yearNum = Number(yyyy.replace('_', '0'))
      if (minDayjs && yearNum < minDayjs.year()) yearNum = minDayjs.year()
      if (maxDayjs && yearNum > maxDayjs.year()) yearNum = maxDayjs.year()
      yyyy = yearNum.toString()
    }

    if (dd) {
      const monthNum = Number(mm)
      const yearNum = Number(yyyy)
      const daysInMonth = dayjs(`${yearNum}-${monthNum}-01`).daysInMonth()
      let dayNum = Number(dd.replace('_', '0'))
      if (dayNum > daysInMonth) dayNum = daysInMonth
      if (dayNum < 1) dayNum = 1
      dd = dayNum.toString().padStart(2, '0')
    }

    const correctedDate = `${mm}/${dd}/${yyyy}`

    if (!isCompleteDate(correctedDate)) {
      setDate(correctedDate)
      return
    }

    const parsed = dayjs(correctedDate, 'MM/DD/YYYY', true)
    if (!parsed.isValid()) {
      return
    }

    let finalDate = parsed
    if (minDayjs && parsed.isBefore(minDayjs)) finalDate = minDayjs
    if (maxDayjs && parsed.isAfter(maxDayjs)) finalDate = maxDayjs

    const formattedDate = finalDate.format('MM/DD/YYYY')
    setDate(formattedDate)
    emitChange(formattedDate, time, ampm)
  }

  const handleTimeChange = (val: string) => {
    const nextTime = clampTimeInput(val)
    setTime(nextTime)
    emitChange(date, nextTime, ampm)
  }

  const handleMeridiemChange = (val: Meridiem) => {
    setAmpm(val)
    emitChange(date, time, val)
  }

  const openCalendar = () => {
    if (disabled) {
      return
    }

    if (datePickerRef.current?.showPicker) {
      datePickerRef.current.showPicker()
    } else {
      datePickerRef.current?.click()
    }
  }

  return (
    <div className="datetime-picker">
      {label && (
        <label className="datetime-main-label">
          {label}
          {required && ' *'}
        </label>
      )}

      <div className="picker-content-wrapper">
        <div className="input-container" style={{ maxWidth: '60%' }}>
          <InputMask
            mask="99/99/9999"
            maskChar="_"
            value={date}
            disabled={disabled}
            onChange={e => handleDateChange(e.target.value)}
            alwaysShowMask={true}
          />
          <input
            ref={datePickerRef}
            type="date"
            className="date-picker"
            min={min ? dayjs(min).tz(ADMIN_DATETIME_TZ).format('YYYY-MM-DD') : undefined}
            max={max ? dayjs(max).tz(ADMIN_DATETIME_TZ).format('YYYY-MM-DD') : undefined}
            onChange={e => handleDatePickerChange(e.target.value)}
          />
          <div
            onClick={openCalendar}
            className="date-picker-icon-wrapper"
            style={{
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <Icon name="CalendarDays" />
          </div>
          <label className={date ? 'filled' : ''}>{dateLabel}</label>
        </div>

        <div className="input-container" style={{ width: 100 }}>
          <InputMask
            mask="99:99"
            maskChar="_"
            value={time}
            disabled={disabled}
            onChange={e => handleTimeChange(e.target.value)}
          />
          <label className={time ? 'filled' : ''}>{timeLabel}</label>
        </div>

        <div className="input-container" style={{ width: 90 }}>
          <select
            value={ampm}
            disabled={disabled}
            onChange={e => handleMeridiemChange(normalizeMeridiem(e.target.value))}
          >
            <option value="am">AM</option>
            <option value="pm">PM</option>
          </select>
          <label className="filled">{meridiemLabel}</label>
        </div>
      </div>
    </div>
  )
}

export default DateTimePicker
